import {
  generateClassName,
  httpSetHelper,
  windowIsDefined,
} from '@klv/shared/utils/functions';
import { useTranslation } from '@klv/hooks/useTranslation';
import { useState } from 'react';
import type { FocusEvent } from 'react';
import * as Yup from 'yup';
import { hasStorageAvailable } from '@klv/shared/utils/storage';
import RoundedText from '@klv/molecules/form-field/rounded-text';
import { gtmBasicEvent } from '@klv/shared/utils/google-tag-manager';
import Button from '@klv/atoms/button';
import { Formik, Form } from 'formik';
import type { FormikProps } from 'formik';
import type { IFormValidationSchema, IInitialFormValues } from '../../organisms/form/types';
import type { ISingleLineForm } from './types';
import * as styles from './styles/singleLineForm.module.scss';
import type { WithOptimizelyProps } from '@optimizely/react-sdk';
import { withOptimizely } from '@optimizely/react-sdk';
import { getCookieValue } from '@klv/shared/utils/cookies';

const initialValues: { company_website: string } = {
  company_website: '',
};

// Custom onBlur handler that only updates form state without tracking
const handleSingleLineFormBlur = (
  event: FocusEvent,
  formik: FormikProps<IInitialFormValues>,
  setFormState: React.Dispatch<React.SetStateAction<{ company_website: string }>>,
) => {
  httpSetHelper(formik, event.target as HTMLInputElement);
  const target = event.target as HTMLInputElement;
  formik.handleBlur(event);
  setFormState((prevState) => ({
    ...prevState,
    [target.name]: target.value,
  }));
};

export const CompanyWebsiteForm = withOptimizely(({
  gradientButton,
  optimizely,
  primaryButtonText,
  placeholderText,
  redirectUrl,
  backgroundColor = 'dark',
}: ISingleLineForm & WithOptimizelyProps) => {
  const { t } = useTranslation();
  const [formState, setFormState] = useState(initialValues);
  const [submitted, setSubmitted] = useState(false);
  const buttonVariant = gradientButton ? 'gradient' : 'primary';
  const currentPath = windowIsDefined() ? 
  window.location.pathname + window.location.search + window.location.hash : '';
  const validationSchemaBase: IFormValidationSchema = {
    company_website: Yup.string()
    .url(t('organisms_forms_fields_company_website_errors_url') || 'Please make sure your company website includes a valid domain (https://example.com)')
    .required(t('organisms_forms_fields_company_website_errors_required') || 'Required'),
  };

  const initialVals: IInitialFormValues = { company_website: formState.company_website };
  const isCustomer = optimizely?.decide('marketing_agent_cta_customer_experience')?.enabled || getCookieValue('hasLoggedInBefore') === 'true';

  return (
    <div className={styles.singleLineFormContainer}>
      <Formik
        initialValues={initialVals}
        validationSchema={Yup.object(validationSchemaBase)}
        validateOnBlur={true}
        onSubmit={async (values) => {
          if (hasStorageAvailable('sessionStorage')) {
            sessionStorage.setItem('formValues', JSON.stringify({ company_website: values.company_website }));
          }
          gtmBasicEvent({
            action: 'launch_agent',
            category: 'marketing_agent',
          });

          if (isCustomer) {
            window.location.assign('/marketing-agent');
          }
          else if (redirectUrl) {
            window.location.assign(redirectUrl);
          }
          else {
            window.location.assign(currentPath);
          }
        }}
      >
        {(formik) => {
          const hascompany_websiteError = submitted && formik.touched.company_website && formik.errors.company_website;

          return (
            <div
              className={generateClassName([
                styles.inputAndButtonContainer,
                hascompany_websiteError && styles.errorBackground,
              ])}
            >
              <Form className={styles.formContainer} id="form--signupRequest">
                <RoundedText
                  errorClassName={styles.inputErrorWebsite}
                  backgroundColor={backgroundColor}
                  id="singleLineForm-companyWebsite"
                  name="company_website"
                  type="url"
                  submitted={submitted}
                  placeholder={placeholderText || `${t('atoms_singleLineForm_placeholderText')}`}
                  onBlur={(event: FocusEvent) => handleSingleLineFormBlur(event, formik, setFormState)}
                />
                <Button
                  className={generateClassName([styles.getStartedButton])}
                  text={primaryButtonText || `${t('atoms_singleLineForm_primaryButton')}`}
                  variant={buttonVariant}
                  type="submit"
                  onFocus={() => {
                    setSubmitted(true);
                  }}
                />
              </Form>
            </div>
          );
        }}
      </Formik>
    </div>
  );
});
